import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

const ICON_TONES = {
  blue:    "bg-blue-50    text-blue-600    ring-blue-100",
  indigo:  "bg-indigo-50  text-indigo-600  ring-indigo-100",
  violet:  "bg-violet-50  text-violet-600  ring-violet-100",
  cyan:    "bg-cyan-50    text-cyan-600    ring-cyan-100",
  emerald: "bg-emerald-50 text-emerald-600 ring-emerald-100",
  amber:   "bg-amber-50   text-amber-600   ring-amber-100",
  rose:    "bg-rose-50    text-rose-600    ring-rose-100",
  slate:   "bg-slate-100  text-slate-600   ring-slate-200",
}

const DELTA_TONES = {
  positive: "text-emerald-600",
  negative: "text-rose-600",
  neutral:  "text-slate-500",
}

export const formatMoney = (cents, symbol) => {
  if (cents == null || isNaN(cents)) return "—"
  return `${(cents / 100).toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })} ${symbol || "€"}`
}

const Tile = ({ icon, iconTone = "blue", label, value, sub, delta }) => (
  <div className="bg-white border border-slate-200 rounded-xl px-4 py-3.5 shadow-sm shadow-slate-900/[0.03]">
    <div className="flex items-start justify-between">
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">
        {label}
      </div>
      <div className={`shrink-0 w-7 h-7 grid place-items-center rounded-lg ring-1 ${ICON_TONES[iconTone] || ICON_TONES.blue}`}>
        <FontAwesomeIcon icon={icon} className="w-3 h-3" />
      </div>
    </div>
    <div className="mt-2 text-[26px] leading-none font-bold tabular-nums text-slate-900">
      {value}
    </div>
    {sub && (
      <div className="mt-1.5 text-[11.5px] text-slate-500 truncate">{sub}</div>
    )}
    {delta && (
      <div className={`mt-1.5 inline-flex items-center gap-1 text-[11.5px] font-medium ${DELTA_TONES[delta.tone] || DELTA_TONES.neutral}`}>
        <FontAwesomeIcon icon={delta.icon || "arrow-trend-up"} className="w-2.5 h-2.5" />
        {delta.label}
      </div>
    )}
  </div>
)

const StatsStrip = ({ stats }) => {
  if (!stats) return null
  const sym = stats.currency_symbol || "€"

  const conversionDelta = (stats.conversion_rate ?? 0) >= 50
    ? { tone: "positive", icon: "arrow-trend-up",  label: `${stats.conversion_rate}%` }
    : (stats.conversion_rate ?? 0) > 0
      ? { tone: "neutral",  icon: "arrow-right",      label: `${stats.conversion_rate}%` }
      : { tone: "negative", icon: "arrow-trend-down", label: "0%" }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
      <Tile
        icon     = "file-lines"
        iconTone = "indigo"
        label    = {I18n.t("quotes.project_index.stats.count_label")}
        value    = {String(stats.count ?? 0)}
      />
      <Tile
        icon     = "calculator"
        iconTone = "blue"
        label    = {I18n.t("quotes.project_index.stats.subtotal_label")}
        value    = {formatMoney(stats.subtotal_cents, sym)}
      />
      <Tile
        icon     = "circle-check"
        iconTone = "emerald"
        label    = {I18n.t("quotes.project_index.stats.accepted_label")}
        value    = {String(stats.accepted_count ?? 0)}
        sub      = {formatMoney(stats.accepted_cents, sym)}
      />
      <Tile
        icon     = "paper-plane"
        iconTone = "amber"
        label    = {I18n.t("quotes.project_index.stats.pending_label", { defaultValue: "Envoyés en attente" })}
        value    = {String(stats.pending_count ?? 0)}
        sub      = {formatMoney(stats.pending_cents, sym)}
      />
      <Tile
        icon     = "hard-hat"
        iconTone = "violet"
        label    = {I18n.t("quotes.project_index.stats.converted_label")}
        value    = {String(stats.converted_count ?? 0)}
        delta    = {conversionDelta}
      />
      <Tile
        icon     = "percent"
        iconTone = "cyan"
        label    = {I18n.t("quotes.project_index.stats.conversion_rate", { defaultValue: "Taux de conversion" })}
        value    = {`${stats.conversion_rate ?? 0}%`}
      />
    </div>
  )
}

export default StatsStrip
